/**
 * ATL-1103 — the two shapes an order / supply-order status can arrive in, in one place.
 *
 * Firestore holds a status OBJECT:
 *
 *     orders.status       = { id: 'canceled',  isProcessed: true, text: 'Заказ отменен' }
 *     supplyOrders.status = { id: 'completed', isCompleted: true, text: 'supplyOrders.statusCompleted' }
 *
 * The Supabase read path returns the same object (the column is `jsonb` as of
 * `20260901140000_atl1103_order_status_jsonb.sql`), but it can also return a BARE STATUS ID —
 * a JSON string — because some rows genuinely never had a map: a marketplace order is created as
 * `'pending'`, and before ATL-1103 the update path reduced the object to `status.id` to fit a
 * `text` column, which flattened 3,939 orders and 9 supply orders.
 *
 * Both shapes are permanent and both must render. What must NEVER happen is the reverse: a screen
 * seeded from a flattened value writing that value back over the map. `SupplyOrderDetailScreen`
 * did exactly that — `handleSave` writes `status` and `isCompleted: status.isCompleted` on ANY
 * save, so a COMPLETED supply order read as a draft would be saved as a draft, into Firestore,
 * dual-written, permanently. Firestore was the rollback copy and it was the thing overwritten.
 *
 * Mirrors `apps/atlas-backend/src/common/utils/order-status.util.ts`. Keep the two in step.
 */

/** An option in a screen's status table: the canonical `{ id, text, … }` tuple. */
export interface StatusOption {
  id: string
  text: string
  [key: string]: unknown
}

/** What a status field can hold, on either read path. */
export type StatusLike = Record<string, unknown> | string | null | undefined

/**
 * Is this the status MAP rather than a bare id?
 *
 * Exported since ATL-1143: `complexSaleStatus.ts` builds on these primitives, which are
 * domain-agnostic despite this file's name.
 */
export const isStatusMap = (status: StatusLike): status is Record<string, unknown> =>
  typeof status === 'object' && status !== null && !Array.isArray(status)

/**
 * The status id, from either shape.
 *
 * Deliberately identical to the `status_id` GENERATED column in Supabase:
 * object → `status->>'id'`, string → the string itself, anything else → null.
 */
export const statusId = (status: StatusLike): string | null => {
  if (typeof status === 'string') return status || null
  if (isStatusMap(status)) return typeof status.id === 'string' ? status.id : null
  return null
}

/**
 * The canonical option this status refers to, or the value itself when it refers to none.
 *
 * - a map or id naming a known option → THE OPTION, so `text` is the current locale's rather than
 *   whatever locale happened to be active when the row was written;
 * - a map naming no known option → the MAP, verbatim. Unknown is not a reason to discard: this is
 *   the value that gets written back, and replacing it would be the destruction, not the fix;
 * - nothing usable → `null`, so the caller decides what an absent status means rather than
 *   inheriting a silent default.
 *
 * It never returns a bare string, which is the property the write-back depends on.
 */
export const resolveStatusOption = <T extends StatusOption>(
  status: StatusLike,
  options: readonly T[],
): T | Record<string, unknown> | null => {
  const id = statusId(status)
  if (id !== null) {
    const match = options.find((o) => o.id === id)
    if (match) return match
  }
  if (isStatusMap(status)) return status
  return null
}

/**
 * The text to show for a status, or `null` when nothing can be shown honestly.
 *
 * `translate` is OPTIONAL and that asymmetry is real, not an oversight: a supply-order status
 * carries a translation KEY (`'supplyOrders.statusCompleted'`) so its screens pass `t`, while a
 * client-order status carries an already-translated string (the table is built with `t(...)` at
 * render time) so its screens pass nothing. Translating both would translate the client label
 * twice.
 *
 * An id that matches no option renders AS ITSELF rather than falling through to a default label.
 * A marketplace order legitimately carries `'processing'`, which is in no screen's table — showing
 * "new" for it is the same class of lie this ticket is about, just smaller.
 *
 * Returning `null` — rather than a default — when there is no status at all is the point.
 * `item?.status?.text ?? t('…statusNew')` rendered a hardcoded "new" for EVERY order the moment
 * `status` stopped being an object, so a CANCELLED order read as new. A caller that wants a
 * fallback must now say so.
 */
export const statusDisplayText = (
  status: StatusLike,
  options: readonly StatusOption[],
  translate?: (key: string) => string,
): string | null => {
  const resolved = resolveStatusOption(status, options)
  if (resolved !== null) {
    const text = (resolved as Record<string, unknown>).text
    if (typeof text === 'string' && text) return translate ? translate(text) : text
  }
  // No option and no text — but an id is still something true to show.
  return statusId(resolved === null ? status : (resolved as StatusLike))
}

/**
 * Does this status mean the supply order is finished?
 *
 * The map's own `isCompleted` wins where it exists — it is what the till wrote. Otherwise the id
 * decides, using the same table the writer uses. 24 supply orders in prod hold
 * `{id: 'completed', text: '…'}` with NO `isCompleted` key, so the fallback is not hypothetical.
 *
 * `null` means "this status says nothing about completion" — never `false`. An absent field that
 * answers `false` is the ATL-1020 defect, and here it would mark a completed order incomplete.
 */
export const COMPLETED_SUPPLY_ORDER_STATUS_IDS: readonly string[] = ['completed', 'cancelled']

export const supplyOrderIsCompleted = (status: StatusLike): boolean | null => {
  if (isStatusMap(status) && typeof status.isCompleted === 'boolean') return status.isCompleted
  const id = statusId(status)
  if (id === null) return null
  return COMPLETED_SUPPLY_ORDER_STATUS_IDS.includes(id)
}

/** Order status ids that mean "not yet processed" — the Firestore `isProcessed: false` set. */
export const UNPROCESSED_ORDER_STATUS_IDS: readonly string[] = [
  'new',
  'contacted',
  'confirmed',
  'pending',
  'processing',
]

/** As above, for a client order. `null` when the status says nothing. */
export const orderIsProcessed = (status: StatusLike): boolean | null => {
  if (isStatusMap(status) && typeof status.isProcessed === 'boolean') return status.isProcessed
  const id = statusId(status)
  if (id === null) return null
  return !UNPROCESSED_ORDER_STATUS_IDS.includes(id)
}

/**
 * The value to WRITE for a status. Never a bare string, never a collapse.
 *
 * A map goes back as the map. A bare id is expanded to the canonical option when one matches —
 * that is the one direction in which reconstruction is legitimate, because the option table is
 * where the writer got the map from in the first place, so this restores a value the app itself
 * wrote rather than inventing one. An id matching nothing stays `{ id }` — still a map, so the
 * next reader sees an object, and still carrying no text nobody chose.
 */
export const statusForWrite = <T extends StatusOption>(
  status: StatusLike,
  options: readonly T[],
): Record<string, unknown> | null => {
  const resolved = resolveStatusOption(status, options)
  if (resolved !== null) return resolved as Record<string, unknown>
  const id = statusId(status)
  return id === null ? null : { id }
}

/**
 * The client-order status table — the single definition the list screen, the detail screen and the
 * backend's `UNPROCESSED_ORDER_STATUS_IDS` all agree with.
 *
 * A factory rather than a constant because `text` is TRANSLATED at write time (unlike a supply
 * order's, which is a translation key), so it depends on the active locale.
 */
export const clientOrderStatuses = (t: (key: string) => string): StatusOption[] => [
  { id: 'new', text: t('clientOrders.statusNew'), isProcessed: false },
  { id: 'contacted', text: t('clientOrders.statusContacted'), isProcessed: false },
  { id: 'confirmed', text: t('clientOrders.statusConfirmed'), isProcessed: false },
  { id: 'delivered', text: t('clientOrders.statusDelivered'), isProcessed: true },
  { id: 'canceled', text: t('clientOrders.statusCanceled'), isProcessed: true },
]

/**
 * The supply-order status table. `text` is a translation KEY here, so the table is static and the
 * screens translate at render time.
 */
export const SUPPLY_ORDER_STATUSES: StatusOption[] = [
  { id: 'draft', isCompleted: false, text: 'supplyOrders.statusDraft' },
  { id: 'sent', isCompleted: false, text: 'supplyOrders.statusSent' },
  { id: 'completed', isCompleted: true, text: 'supplyOrders.statusCompleted' },
  { id: 'cancelled', isCompleted: true, text: 'supplyOrders.statusCancelled' },
]
